import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Target } from 'lucide-react';

const Sports = () => {
  const [games, setGames] = useState([]);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const response = await fetch('/api/games');
        if (!response.ok) {
          throw new Error('Failed to fetch games');
        }
        const data = await response.json();
        console.log('Fetched data:', data);
        setGames(data);
      } catch (error) {
        console.error('Error fetching games:', error);
      }
    };

    fetchGames();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 },
    },
  };

  if (games.length === 0) {
    return (
      <section className="pt-24 sm:pt-20 pb-12 px-4 sm:px-6 bg-gray-50 dark:bg-gray-900 backdrop-blur-lg transition-colors duration-500 min-h-screen">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Loading games...</h1>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-24 sm:pt-20 pb-12 px-4 sm:px-6 bg-gray-50 dark:bg-gray-900 backdrop-blur-lg transition-colors duration-500 min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Sports Tipping - The Super Tip",
            "description": "Explore live sports betting odds, place tips on your favorite games, and win big with The Super Tip platform.",
            "url": "https://thesupertip.com/sports",
            "mainEntity": games.map(game => ({
              "@type": "SportsEvent",
              "@id": `https://thesupertip.com/game/${game.id}`,
              "name": game.match,
              "description": `${game.sport} match between ${game.match.split(' vs ')[0]} and ${game.match.split(' vs ')[1]}`,
              "sport": game.sport,
              "startDate": game.startDate,
              "eventStatus": game.eventStatus,
              "location": {
                "@type": "VirtualLocation",
                "url": "https://thesupertip.com/sports"
              },
              "offers": [
                {
                  "@type": "Offer",
                  "name": "Home Win",
                  "price": game.odds.home,
                  "priceCurrency": "USD"
                },
                {
                  "@type": "Offer",
                  "name": "Draw",
                  "price": game.odds.draw,
                  "priceCurrency": "USD"
                },
                {
                  "@type": "Offer",
                  "name": "Away Win",
                  "price": game.odds.away,
                  "priceCurrency": "USD"
                }
              ]
            }))
          })
        }}
      />
      <div className="max-w-7xl mx-auto">
        <motion.h1
          className="title-shine text-4xl md:text-5xl lg:text-6xl font-display font-semibold text-black dark:text-gold uppercase tracking-wide text-center mt-20 mb-12 title-shadow"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Sports Tipping
        </motion.h1>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6 sm:gap-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {games.map((game) => (
            <motion.div
              key={game.id}
              className="group relative z-10 cursor-pointer hover:-translate-y-2 hover:rotate-1 hover:scale-105 hover:shadow-2xl hover:shadow-gold/30 shadow-lg shadow-black/10 transition-all duration-500 ease-out backdrop-blur-md bg-white/70 dark:bg-gray-800/50 rounded-2xl p-6 border border-white/30 dark:border-gold/20 hover:border-gold/40"
              variants={itemVariants}
              whileHover={{
                y: -8,
                rotateY: 5,
                rotateX: 5,
              }}
              whileTap={{ scale: 0.98 }}
              style={{
                transformStyle: "preserve-3d",
                perspective: "1000px"
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Target className="h-6 w-6 text-yellow-500" />
                  <span className="text-lg font-serif font-semibold text-gray-900 dark:text-gray-textdark">
                    {game.sport}
                  </span>
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">{new Date(game.date).toLocaleString()}</span>
              </div>

              <h3 className="text-xl font-sans font-bold text-gray-900 dark:text-gray-textdark mb-4">
                {game.match}
              </h3>

              <div className="grid grid-cols-3 gap-3">
                {['Home', 'Draw', 'Away'].map((option, index) => {
                  const oddsKey = option.toLowerCase() === 'away' ? 'away' : option.toLowerCase() === 'draw' ? 'draw' : 'home';
                  const odds = game.odds[oddsKey];
                  const recommended = game.tip === '1' ? 'Home' : game.tip === 'X' ? 'Draw' : game.tip === '2' ? 'Away' : null;
                  const isBest = option === recommended;

                  return (
                    <div key={option} className={`text-center p-3 rounded-lg transition-all duration-300 ${isBest ? 'bg-gold/10 border-2 border-gold/30 dark:bg-gold/5' : 'bg-white/50 dark:bg-gray-700/30'}`}>
                      <p className={`text-sm font-body font-medium mb-1 ${isBest ? 'text-gold font-bold' : 'text-gray-900 dark:text-gray-textdark'}`}>{option}</p>
                      <p className={`text-lg font-bold mb-3 ${isBest ? 'text-gold font-extrabold text-xl' : 'text-yellow-500'}`}>{odds}</p>
                      <motion.button
                        className={`w-full bg-gradient-to-r ${isBest ? 'from-gold to-goldlight' : 'from-goldlight to-gold'} text-black py-3 px-3 min-h-[44px] rounded-lg hover:shadow-gold/40 hover:shadow-lg transition-all duration-300 text-sm font-body font-bold touch-manipulation ${isBest ? 'hover:scale-105 hover:brightness-110' : 'hover:scale-102'}`}
                        whileHover={{
                          scale: isBest ? 1.05 : 1.02,
                          boxShadow: `0 10px 25px rgba(255, 214, 10, 0.4)`
                        }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => alert(`Tip placed on ${option} for ${game.match}`)}
                      >
                        TIP {option.toUpperCase()}
                      </motion.button>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Sports;
